import { useTranslation } from 'react-i18next';

import { Answer } from '@/components/question/forms/shared/Answer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Divider } from '@/components/ui/daisy';
import {
  AnswerMode,
  useAnswerMode,
} from '@/data/hook/question/answers/useAnswerMode';
import { PositionalAnswerCreateDTO } from '@/models/Answer';
import usePedWorkspace from '@/data/hook/ped-content/usePedWorkspace';

type CombinationAnswerItemProps = {
  item: PositionalAnswerCreateDTO;
  totalQuestions: number;
  initialEditing?: boolean;
  onUpdateText: (text: string) => void;
  onUpdateMedia: (file: File | string, altText?: string) => void;
  onUpdateValidPositions: (positions: number[]) => void;
  onMarkIncorrect: () => void;
  onDelete: () => void;
};

export default function CombinationAnswerItem({
  item,
  totalQuestions,
  initialEditing,
  onUpdateText,
  onUpdateMedia,
  onUpdateValidPositions,
  onMarkIncorrect,
  onDelete,
}: CombinationAnswerItemProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'courseLineForm.questionsTab.createQuestionModal.combinationForm',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });

  const {
    mode,
    isEditing,
    handleSwitchToMedia,
    handleSwitchToText,
    handleStartEdit,
    handleFinishEdit,
  } = useAnswerMode({
    mediaFile: item.mediaFile,
    mediaLink: item.mediaLink,
    initialEditing,
  });

  const isCorrect = item.validPositions.length > 0;
  const { isLoading, isLimitedEdition } = usePedWorkspace();

  return (
    <Answer
      isCorrect={isCorrect}
      isEditing={isEditing}
      isMedia={mode === 'media'}
      className="w-auto min-w-[157px] px-3"
    >
      <AnswerContent
        mode={mode}
        isEditing={isEditing}
        item={item}
        onUpdateMedia={onUpdateMedia}
        onUpdateText={onUpdateText}
        onFinishEdit={handleFinishEdit}
      />
      <Answer.MoreOptions>
        <Answer.MoreOptions.Positions
          totalPositions={totalQuestions}
          selectedPositions={item.validPositions}
          onChange={onUpdateValidPositions}
          header={tCommon('correctIn')}
          getPositionLabel={position =>
            t('itemLabel', { number: String(position).padStart(2, '0') })
          }
        />
        <Divider className="my-0 py-4" />
        <Answer.MoreOptions.EditText
          onClick={mode === 'text' ? handleStartEdit : handleSwitchToText}
          disabled={isLoading}
        />
        <Answer.MoreOptions.EditMedia
          onClick={mode === 'media' ? handleStartEdit : handleSwitchToMedia}
          disabled={isLimitedEdition || isLoading}
        />
        <ConditionalRenderer condition={isCorrect}>
          <Answer.MoreOptions.MarkIncorrect
            onClick={onMarkIncorrect}
            disabled={isLimitedEdition || isLoading}
          />
        </ConditionalRenderer>
        <Answer.MoreOptions.Delete
          onClick={onDelete}
          disabled={isLimitedEdition || isLoading}
        />
      </Answer.MoreOptions>
    </Answer>
  );
}

type AnswerContentProps = {
  mode: AnswerMode;
  isEditing: boolean;
  onFinishEdit: () => void;
} & Pick<CombinationAnswerItemProps, 'item' | 'onUpdateMedia' | 'onUpdateText'>;

const AnswerContent = ({
  mode,
  isEditing,
  item,
  onUpdateMedia,
  onUpdateText,
  onFinishEdit,
}: AnswerContentProps) => {
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.answersCommon',
  });

  if (mode === 'media') {
    return (
      <ConditionalRenderer
        condition={isEditing}
        fallback={
          <Answer.MediaPreview
            mediaFile={item.mediaFile}
            mediaLink={item.mediaLink}
            altText={item.altText ?? undefined}
            placeholder={tCommon('newOption')}
          />
        }
      >
        <Answer.MediaInput
          initialMediaFile={item.mediaFile}
          initialAltText={item.altText ?? ''}
          onConfirm={(file, altText) => {
            onUpdateMedia(file, altText);
            onFinishEdit();
          }}
          onCancel={onFinishEdit}
        />
      </ConditionalRenderer>
    );
  }

  return (
    <ConditionalRenderer
      condition={isEditing}
      fallback={<Answer.TextPreview text={item.text} />}
    >
      <Answer.TextInput
        initialValue={item.text ?? ''}
        placeholder={tCommon('newOption')}
        onConfirm={value => {
          onUpdateText(value);
          onFinishEdit();
        }}
        onCancel={onFinishEdit}
      />
    </ConditionalRenderer>
  );
};
